import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchQaModelStats } from './api';
import { formatCount, formatSeconds } from './format';
import {
  DEFAULT_INTERVAL,
  RANGE_MS,
  type IntervalKey,
  type RangeKey,
  bucketAttempts,
  recentErrors,
  slowestRequests,
} from './servedModelAnalytics';
import type { QaServedModelAttempt, QaServedModelStats } from './types';

const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24h',
  '7d': '7 days',
  '30d': '30 days',
};

const INTERVAL_LABELS: Record<IntervalKey, string> = {
  '15m': '15 min',
  '1h': '1 hour',
  '6h': '6 hours',
  '1d': '1 day',
};

const COLOR_OK = '#70B77E';
const COLOR_ERROR = '#F0544F';
const COLOR_P50 = '#6366f1';
const COLOR_P95 = '#0ea5e9';
const COLOR_RPS = '#8b5cf6';
const COLOR_CONCURRENT = '#f59e0b';

const SOURCE_COLORS: Record<string, string> = {
  interior: '#6366f1',
  video: '#0ea5e9',
  ads: '#f59e0b',
  mobile: '#10b981',
  lightning: '#8b5cf6',
  merch: '#ec4899',
  stress: '#f97316',
  unknown: '#94a3b8',
};

const FALLBACK_SOURCE_COLORS = ['#14b8a6', '#e11d48', '#84cc16', '#a855f7'];

function sourceColor(source: string, index: number): string {
  return SOURCE_COLORS[source] ?? FALLBACK_SOURCE_COLORS[index % FALLBACK_SOURCE_COLORS.length];
}

function formatModelName(name: string): string {
  return name.replace(/_/g, ' ');
}

function formatSource(source: string): string {
  return source.replace(/_/g, ' ');
}

function percentile(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  if (sorted.length === 1) return sorted[0];
  const position = p * (sorted.length - 1);
  const low = Math.floor(position);
  const high = Math.min(low + 1, sorted.length - 1);
  const weight = position - low;
  return sorted[low] * (1 - weight) + sorted[high] * weight;
}

function statsForVersion(
  model: QaServedModelStats,
  modelVersion: string,
): QaServedModelStats {
  if (modelVersion === 'all') return model;
  const attempts = model.attempts.filter(
    (attempt) => attempt.model_version === modelVersion,
  );
  const durations = attempts
    .filter(
      (attempt) => attempt.ok && attempt.duration_seconds != null,
    )
    .map((attempt) => attempt.duration_seconds as number)
    .sort((a, b) => a - b);
  const sources = new Map<string, number>();
  const attemptsByNumber = new Map<number, number>();
  for (const attempt of attempts) {
    sources.set(attempt.source, (sources.get(attempt.source) ?? 0) + 1);
    if (attempt.attempt_number != null && attempt.attempt_number > 0) {
      attemptsByNumber.set(
        attempt.attempt_number,
        (attemptsByNumber.get(attempt.attempt_number) ?? 0) + 1,
      );
    }
  }
  return {
    ...model,
    calls: attempts.length,
    jobs: new Set(
      attempts.map((attempt) => attempt.job_id).filter(Boolean),
    ).size,
    errors: attempts.filter((attempt) => !attempt.ok).length,
    retried_attempts: attempts.filter(
      (attempt) => (attempt.attempt_number ?? 0) > 1,
    ).length,
    duration_seconds: {
      count: durations.length,
      min: durations[0] ?? null,
      max: durations[durations.length - 1] ?? null,
      mean:
        durations.length > 0
          ? durations.reduce((sum, duration) => sum + duration, 0) /
            durations.length
          : null,
      p50: percentile(durations, 0.5),
      p90: percentile(durations, 0.9),
      p95: percentile(durations, 0.95),
      p99: percentile(durations, 0.99),
    },
    sources: [...sources.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([source, count]) => ({ source, count })),
    attempts_by_number: [...attemptsByNumber.entries()]
      .sort(([left], [right]) => left - right)
      .map(([attempt_number, count]) => ({ attempt_number, count })),
    attempts,
  };
}

function chartTooltipStyle(): Record<string, string> {
  return {
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    fontSize: '12px',
  };
}

function Stat({
  label,
  value,
  hint,
  tone = 'slate',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'slate' | 'red';
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-soft">
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          tone === 'red' ? 'text-red-600' : 'text-slate-900'
        }`}
        title={hint}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[11px] text-slate-400">{hint}</div>}
    </div>
  );
}

function AttemptRow({
  attempt,
  right,
}: {
  attempt: QaServedModelAttempt;
  right: string;
}) {
  return (
    <li className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] text-slate-500 truncate">
          {(attempt.job_id || attempt.id || '—').slice(0, 8)}
        </span>
        <span className="text-xs font-medium text-slate-700 shrink-0">{right}</span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-slate-400">
        <span className="capitalize">{formatSource(attempt.source)}</span>
        <span>
          {attempt.started_at
            ? formatDistanceToNow(new Date(attempt.started_at), { addSuffix: true })
            : ''}
        </span>
      </div>
      {attempt.error && (
        <p className="mt-1 text-xs text-slate-700 line-clamp-2 break-words">{attempt.error}</p>
      )}
    </li>
  );
}

function ModelCard({
  model,
  rangeKey,
  interval,
}: {
  model: QaServedModelStats;
  rangeKey: RangeKey;
  interval: IntervalKey;
}) {
  const [expanded, setExpanded] = useState(false);
  const [modelVersion, setModelVersion] = useState('all');
  const availableVersions = useMemo(
    () => [
      ...new Set(model.attempts.map((attempt) => attempt.model_version)),
    ].sort(),
    [model.attempts],
  );
  useEffect(() => {
    if (
      modelVersion !== 'all' &&
      !availableVersions.includes(modelVersion)
    ) {
      setModelVersion('all');
    }
  }, [availableVersions, modelVersion]);
  const displayedModel = useMemo(
    () => statsForVersion(model, modelVersion),
    [model, modelVersion],
  );
  const duration = displayedModel.duration_seconds;
  const errorRate =
    displayedModel.calls > 0
      ? Math.round(
          (1000 * displayedModel.errors) / displayedModel.calls,
        ) / 10
      : 0;
  const buckets = useMemo(
    () => bucketAttempts(displayedModel.attempts, rangeKey, interval),
    [displayedModel.attempts, rangeKey, interval],
  );
  const errors = useMemo(
    () => recentErrors(displayedModel.attempts),
    [displayedModel.attempts],
  );
  const slowest = useMemo(
    () => slowestRequests(displayedModel.attempts),
    [displayedModel.attempts],
  );
  const pieData = displayedModel.sources.map((row) => ({
    name: formatSource(row.source),
    value: row.count,
    source: row.source,
  }));

  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-soft overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 capitalize">
            {formatModelName(model.model_name)}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">{model.model_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            Version
            <select
              aria-label={`Model version for ${model.model_name}`}
              value={modelVersion}
              onChange={(event) => setModelVersion(event.target.value)}
              className="rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="all">All versions</option>
              {availableVersions.map((version) => (
                <option key={version} value={version}>
                  {version}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setExpanded((open) => !open)}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50"
            aria-expanded={expanded}
          >
            {expanded ? 'Hide details' : 'Show details'}
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-5">
        <Stat
          label="Calls"
          value={formatCount(displayedModel.calls)}
          hint={`${formatCount(displayedModel.jobs)} job${displayedModel.jobs === 1 ? '' : 's'}`}
        />
        <Stat
          label="Latency"
          value={formatSeconds(duration.p95)}
          hint={`p50 ${formatSeconds(duration.p50)} · p95`}
        />
        <Stat
          label="Errors"
          value={formatCount(displayedModel.errors)}
          hint={`${errorRate}% of calls`}
          tone={displayedModel.errors > 0 ? 'red' : 'slate'}
        />
      </div>

      <div className="px-5 pb-5 grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Call source</h3>
          <div className="h-52">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={42}
                    outerRadius={68}
                    paddingAngle={2}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={entry.source} fill={sourceColor(entry.source, index)} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={chartTooltipStyle()}
                    formatter={(value: number, name: string) => [`${value} calls`, name]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={24}
                    formatter={(value: string) => (
                      <span className="text-slate-600 text-xs capitalize">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">
                No sources in this period
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-xl border border-slate-200 p-4 bg-slate-50/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Request counts</h3>
            <div className="text-right">
              <span className="text-lg font-bold text-slate-700">{displayedModel.calls}</span>
              <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
            </div>
          </div>
          <div className="h-52">
            {displayedModel.calls > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: '#64748b' }}
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip contentStyle={chartTooltipStyle()} />
                  <Legend
                    verticalAlign="bottom"
                    height={24}
                    formatter={(value: string) => (
                      <span className="text-slate-600 text-xs">{value}</span>
                    )}
                  />
                  <Bar dataKey="ok" name="Success" stackId="a" fill={COLOR_OK} />
                  <Bar
                    dataKey="error"
                    name="Error"
                    stackId="a"
                    fill={COLOR_ERROR}
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">
                No requests in this period
              </div>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-slate-100 pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartPanel title="Latency over time">
              <LineChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip
                  contentStyle={chartTooltipStyle()}
                  formatter={(value, name) => [
                    formatSeconds(typeof value === 'number' ? value : null),
                    String(name),
                  ]}
                />
                <Legend
                  verticalAlign="bottom"
                  height={24}
                  formatter={(value: string) => (
                    <span className="text-slate-600 text-xs">{value}</span>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="p50"
                  name="p50"
                  stroke={COLOR_P50}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="p95"
                  name="p95"
                  stroke={COLOR_P95}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ChartPanel>
            <ChartPanel title="Processed requests / s">
              <LineChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip contentStyle={chartTooltipStyle()} />
                <Line
                  type="monotone"
                  dataKey="rps"
                  name="req/s"
                  stroke={COLOR_RPS}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartPanel>
            <ChartPanel title="Concurrent requests">
              <LineChart data={buckets} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                <Tooltip contentStyle={chartTooltipStyle()} />
                <Line
                  type="monotone"
                  dataKey="concurrent"
                  name="in flight"
                  stroke={COLOR_CONCURRENT}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartPanel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800">Recent errors</h3>
                <div className="text-right">
                  <span className="text-lg font-bold text-[#F0544F]">{displayedModel.errors}</span>
                  <span className="ml-1 text-[10px] text-slate-500 uppercase">total</span>
                </div>
              </div>
              {errors.length > 0 ? (
                <ul className="space-y-2 overflow-y-auto max-h-64 pr-1">
                  {errors.map((attempt, index) => (
                    <AttemptRow
                      key={attempt.id || `${attempt.job_id}-${index}`}
                      attempt={attempt}
                      right={formatSeconds(attempt.duration_seconds)}
                    />
                  ))}
                </ul>
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-[#70B77E]">
                  No errors in this period
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800">Slower requests</h3>
                <div className="text-right">
                  <span className="text-lg font-bold text-slate-700">
                    {formatSeconds(duration.p90)}
                  </span>
                  <span className="ml-1 text-[10px] text-slate-500 uppercase">p90</span>
                </div>
              </div>
              {slowest.length > 0 ? (
                <ul className="space-y-2 overflow-y-auto max-h-64 pr-1">
                  {slowest.map((attempt, index) => (
                    <AttemptRow
                      key={attempt.id || `${attempt.job_id}-slow-${index}`}
                      attempt={attempt}
                      right={formatSeconds(attempt.duration_seconds)}
                    />
                  ))}
                </ul>
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
                  No successful requests in this period
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function ChartPanel({
  title,
  children,
}: {
  title: string;
  children: ReactElement;
}) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/50">
      <h3 className="text-sm font-semibold text-slate-800 mb-3">{title}</h3>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/**
 * Per-model statistics for the self-hosted model APIs that generation
 * pipelines call. Numbers come from `model_api_attempts`, which interior (and
 * later other pipelines) write — not from the serving process itself.
 */
export function ServedModelsPage() {
  const [rangeKey, setRangeKey] = useState<RangeKey>('7d');
  const [interval, setInterval] = useState<IntervalKey>(DEFAULT_INTERVAL['7d']);
  const [includeStress, setIncludeStress] = useState(false);
  const [models, setModels] = useState<QaServedModelStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const to = new Date();
    const from = new Date(to.getTime() - RANGE_MS[rangeKey]);
    fetchQaModelStats(from, to, includeStress)
      .then((data) => {
        if (!cancelled) {
          setModels(data.models);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [includeStress, rangeKey]);

  const totals = useMemo(
    () => ({
      calls: models.reduce((sum, model) => sum + model.calls, 0),
      errors: models.reduce((sum, model) => sum + model.errors, 0),
    }),
    [models],
  );

  function selectRange(key: RangeKey) {
    setRangeKey(key);
    setInterval(DEFAULT_INTERVAL[key]);
  }

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Served models</h1>
          <p className="text-sm text-slate-500 mt-1">
            Calls, latency and errors for each model API the generation pipelines
            call.
            {models.length > 0
              ? ` ${formatCount(totals.calls)} call${totals.calls === 1 ? '' : 's'}, ${formatCount(totals.errors)} error${totals.errors === 1 ? '' : 's'}.`
              : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start">
          <label className="flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={includeStress}
              onChange={(event) => setIncludeStress(event.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Include stress calls
          </label>
          <div className="flex rounded border border-slate-200 overflow-hidden">
            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => selectRange(key)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  rangeKey === key
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {RANGE_LABELS[key]}
              </button>
            ))}
          </div>
          <select
            value={interval}
            onChange={(e) => setInterval(e.target.value as IntervalKey)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            title="Bucket interval"
          >
            {(Object.keys(INTERVAL_LABELS) as IntervalKey[]).map((key) => (
              <option key={key} value={key}>
                {INTERVAL_LABELS[key]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading model stats…</div>
      ) : models.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-soft px-5 py-8 text-center text-sm text-slate-500">
          No model API attempts in this window.
          {!includeStress ? ' Stress calls are hidden.' : ''}
        </div>
      ) : (
        <div className="space-y-4">
          {models.map((model) => (
            <ModelCard
              key={model.model_name}
              model={model}
              rangeKey={rangeKey}
              interval={interval}
            />
          ))}
        </div>
      )}
    </div>
  );
}
